import { useSyncExternalStore, useCallback } from "react";
import type { LifecycleStageId } from "./lifecycle";

export interface ActiveContext {
    mode: LifecycleStageId | "default";
    source: string;
    jobId?: string;
}

const STORAGE_KEY = "gc_active_context";

const DEFAULT_CONTEXT: ActiveContext = { mode: "default", source: "" };

function getSnapshot(): ActiveContext {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as ActiveContext) : DEFAULT_CONTEXT;
    } catch {
        return DEFAULT_CONTEXT;
    }
}

let _cache = getSnapshot();
const listeners = new Set<() => void>();

function notify() {
    _cache = getSnapshot();
    listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

function snapshot() {
    return _cache;
}

function setActiveContext(mode: LifecycleStageId | "default", source: string, jobId?: string) {
    const ctx: ActiveContext = { mode, source, jobId };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    notify();
}

function clearActiveContext() {
    sessionStorage.removeItem(STORAGE_KEY);
    notify();
}

export function useActiveContext() {
    const ctx = useSyncExternalStore(subscribe, snapshot, () => DEFAULT_CONTEXT);

    const setContext = useCallback(
        (mode: LifecycleStageId | "default", source: string, jobId?: string) => setActiveContext(mode, source, jobId),
        [],
    );

    const clearContext = useCallback(() => clearActiveContext(), []);

    return { context: ctx, setContext, clearContext };
}
